import { ActivatedRouteSnapshot, CanActivateFn, Route, RouterStateSnapshot, Routes, UrlSegment } from "@angular/router";
import { PROJECT_ROUTES } from "@features/project/constants/routes.constant";
import { CUSTOMER_ROUTES } from "@features/customer/constants/routes.constant";
import { ADMINISTRATION_ROUTES } from "@features/administration/constants/routes.constant";
import { keycloakGuard } from "@ogs/ngx-keycloak";
import { CustomerModule } from "@features/customer/customer.module";
import { SecureComponent } from "../components/layouts/secure/secure.component";
import { EMPTY_ROUTE, NOT_FOUND_ROUTE, WILDCARD_ROUTE } from "@shared/common/routes.common";
import { ExtjsWildcardRouteComponent } from "@core/components/pages/extjs/extjs-wildcard-route.component";
import { AUTHENTICATE_ROUTES_DEFINITION } from "@features/authenticate/constants/routes.constant";
import { ExtjsService } from "@core/services/extjs/extjs.service";
import { inject } from "@angular/core";

/* eslint-disable-next-line @unicorn/prefer-array-some */
export const hasPathInSegment: (check: string) => CanActivateFn = (check: string) => (route: ActivatedRouteSnapshot, _: RouterStateSnapshot) => Boolean(route.url.find((segment: UrlSegment) => segment.path === check));
export const DEACTIVATE_PREFIX: string = "deactivated_";

const canActivateFn = (): boolean => false;

export const deactivate = (route: Route): Route => {
  route.path = `${ DEACTIVATE_PREFIX }${ route.path }`;
  route.canActivate
    ? route.canActivate.push(canActivateFn)
    : route.canActivate = [ canActivateFn ];

  return route;
};

const breadcrumbTranslationScope: string = "feature/core-routes";
/* eslint-disable @tseslint/typedef */
const LOAD_CHILDREN = {
  project: async (): Promise<typeof PROJECT_ROUTES> => await import("@features/project/constants/routes.constant").then((m) => m.PROJECT_ROUTES),
  customer: async (): Promise<typeof CUSTOMER_ROUTES> => await import("@features/customer/constants/routes.constant").then((m) => m.CUSTOMER_ROUTES),
  administration: async (): Promise<typeof CustomerModule> => await import("@features/customer/customer.module").then((m) => m.CustomerModule),
  authenticate: async (): Promise<typeof AUTHENTICATE_ROUTES_DEFINITION> => await import("@features/authenticate/constants/routes.constant").then((m) => m.AUTHENTICATE_ROUTES_DEFINITION)
};

export const CORE_ROUTES = {
  AUTHENTICATE: "authenticate",
  PROJECTS: "projects",
  CUSTOMERS: "customers",
  ADMINISTRATION: "administration"
};
/* eslint-enable @tseslint/typedef */
export const CORE_ROUTES_DEFINITION: Routes = [
  {
    path: CORE_ROUTES.AUTHENTICATE,
    loadChildren: LOAD_CHILDREN.authenticate
  },
  {
    path: EMPTY_ROUTE,
    component: SecureComponent,
    canActivate: [
      keycloakGuard()
    ],
    children: [
      {
        path: EMPTY_ROUTE,
        redirectTo: CORE_ROUTES.PROJECTS,
        pathMatch: "full"
      },
      {
        path: CORE_ROUTES.PROJECTS,
        loadChildren: LOAD_CHILDREN.project,
        data: {
          routes: () => PROJECT_ROUTES,
          context: {
            breadcrumb: {
              "static": {
                translationInfo: {
                  defaultText: "Alle Projekte",
                  scope: breadcrumbTranslationScope,
                  token: "breadcrumb-label-all-projects"
                }
              }
            }
          }
        }
      },
      {
        path: CORE_ROUTES.CUSTOMERS,
        loadChildren: LOAD_CHILDREN.customer,
        data: {
          routes: () => CUSTOMER_ROUTES,
          context: {
            breadcrumb: {
              "static": {
                translationInfo: {
                  defaultText: "Alle Unternehmen",
                  scope: breadcrumbTranslationScope,
                  token: "breadcrumb-label-all-customers"
                }
              }
            }
          }
        }
      },
      deactivate({
        path: CORE_ROUTES.ADMINISTRATION,
        loadChildren: LOAD_CHILDREN.administration,
        data: {
          routes: () => ADMINISTRATION_ROUTES,
          context: {
            breadcrumb: {
              "static": {
                translationInfo: {
                  defaultText: "Verwaltung",
                  scope: breadcrumbTranslationScope,
                  token: "breadcrumb-label-administration"
                }
              }
            }
          }
        }
      }),
      {
        path: NOT_FOUND_ROUTE,
        redirectTo: CORE_ROUTES.PROJECTS
      },
      {
        path: WILDCARD_ROUTE,
        component: ExtjsWildcardRouteComponent,
        canActivate: [
          (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
            inject(ExtjsService).scope.set("core");

            return !hasPathInSegment("extjs")(route, state);
          }
        ]
      }
    ]
  }
];
